import styled from "@emotion/styled"
import type { TextColor } from "@porsche-design-system/components-react"
import { PButtonPure, PTag, PText } from "@porsche-design-system/components-react"
import {
	colorError,
	colorSuccess,
	radiusSm,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import type { InventoryListItem, StockStatus } from "@slfinrtl/ui-bff-routes"
import { stockStatusMessages, vehicleTypeMessages, vehicleTypeShortMessages } from "@slfinrtl/ui-bff-routes"
import type { MouseEvent, PropsWithChildren, ReactNode } from "react"
import { useCallback, useState } from "react"
import { FormattedMessage, defineMessages, useIntl } from "react-intl"
import { useNavigate } from "react-router"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { ColorIndicator } from "src/common/ColorIndicator"
import { emptyPlaceholder } from "src/common/emptyPlaceholder"
import { isListItemLazy } from "src/common/isListItemLazy/isListItemLazy"
import { commonMessages } from "src/common/messages/common.messages"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import {
	Checkbox,
	CopyToClipboard,
	Date,
	ExteriorColorTile,
	Flex,
	HoverableFlyout,
	InteriorColorTile,
	ListingScoreIcon,
	OnlineOrderingIcon,
	PictureStatic,
	Skeleton,
	StatusBadge,
	TableCellFixed,
	TableRowLink,
	TableRowWithLink,
	TagIcon,
	Tooltip,
	VisibilityIcon,
} from "src/components"
import { useConfig } from "src/hooks"
import { VehicleDetailsStageLink } from "src/screens/vehicleDetails/common/VehicleDetailsStageLink"
import { messages as listMessages } from "./inventoryList.messages"
import { inventoryListColumns } from "./inventoryListColumns.const"
import { PriceHistory } from "./priceHistory/PriceHistory"

const messages = defineMessages({
	addInventoryNumber: {
		defaultMessage: "Add inventory number",
		id: "inventorylist.missingInventoryNumber.link",
	},
	addPrice: {
		defaultMessage: "Add price",
		id: "inventorylist.missingPrice.link",
	},
	copySuccess: {
		defaultMessage: "Copied Offerlink to clipboard.",
		id: "copyOfferUrlToClipboard.success",
	},
	listingQualityTooltipTitle: {
		defaultMessage: "Listing Quality",
		id: "inventoryListFilters.listingQuality.heading",
	},
	vehicleCannotBeShared: {
		defaultMessage: "This vehicle has no listing yet and cannot be shared.",
		id: "inventorylist.sharing.vehicleCannotBeShared",
	},
})

interface InventoryListRowProps {
	actionMenu?: ReactNode
	displayLocation: boolean
	index: number
	isCarSalesLiteEnabled: boolean
	isFirstRegistrationDateAvailable: boolean
	isManagedOfferEnabled?: boolean
	item: InventoryListItem
	onAddPriceClick: () => void
	onAddStockNumberClick: () => void
	// TODO: make onVehicleSelectionToggle and selectedVehicles mandatory after inventory-list-selection-and-sharing
	// feature-toggle is removed
	onVehicleSelectionToggle?: (vin: string) => void
	selectedVehicles?: Set<string>
}

export const InventoryListRow = (props: InventoryListRowProps) => {
	const {
		actionMenu,
		displayLocation,
		index,
		isCarSalesLiteEnabled,
		isFirstRegistrationDateAvailable,
		item,
		onAddPriceClick,
		onAddStockNumberClick,
		onVehicleSelectionToggle,
		selectedVehicles,
		...rest
	} = props
	const { formatMessage } = useIntl()
	const navigate = useNavigate()
	const routes = useDealerAppRoutes()
	const { monroneyLabelEnabled } = useConfig()

	const imageElement = (
		<PictureStatic
			borderRadius={radiusSm}
			aspectRatio="4/3"
			picture={item.image}
			size={160}
			lazy={isListItemLazy(index)}
		/>
	)

	const modelNameElement = item.modelName ? (
		<PText weight="bold" ellipsis title={item.modelName}>
			{item.modelName}
		</PText>
	) : (
		<Skeleton.Text $width="180px" />
	)

	const isVehicleSelected = useCallback((vin: string) => selectedVehicles?.has(vin), [selectedVehicles])

	// to improve rendering performance we render tooltips conditionally when a row is hovered
	// by default there are around 10 tooltips per row (most of them defined below, others deeper via VisibilityIcon or OnlineOrderingIcon)
	// which are rendered for no reason
	const [isHovered, setIsHovered] = useState(false)
	const onMouseEnter = useCallback(() => setIsHovered(true), [setIsHovered])
	const onMouseLeave = useCallback(() => setIsHovered(false), [setIsHovered])

	// to support clicking on tags and icons wrapped in tooltips which are layered above (= higher z-index) the actual link
	const onTableRowClick = useCallback(
		(e: MouseEvent<HTMLElement> & { target: HTMLElement }) => {
			if (e.target.getAttribute("data-state") === "open" && routes?.inventoryList?.vehicleDetails) {
				navigate(routes.inventoryList.vehicleDetails.url(item.id))
			}
		},
		[item.id, navigate, routes],
	)

	return (
		<TableRowWithLink {...rest} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onTableRowClick}>
			<OnTopTableCellFixed $width={inventoryListColumns.actionMenu}>
				<HoverableFlyout
					trigger={<PButtonPure type="button" icon="menu-dots-vertical" hideLabel />}
					align="left"
					width={monroneyLabelEnabled ? "default" : "narrow"}
				>
					{actionMenu}
				</HoverableFlyout>
			</OnTopTableCellFixed>

			<OnTopTableCellFixed
				$width={inventoryListColumns.checkbox}
				onClick={
					onVehicleSelectionToggle && item.sharingId && !isCarSalesLiteEnabled
						? () => (item.sharingId ? onVehicleSelectionToggle(item.sharingId) : undefined)
						: undefined
				}
			>
				<Checkbox
					disabledHint={
						isCarSalesLiteEnabled
							? formatMessage(commonMessages.notAvailableInCarsalesLite)
							: item.sharingId
								? undefined
								: formatMessage(messages.vehicleCannotBeShared)
					}
					checked={item.sharingId ? isVehicleSelected(item.sharingId) : false}
					compact
				/>
			</OnTopTableCellFixed>
			<TableCellFixed $width={inventoryListColumns.modelName}>
				<ImageColorModelWrapper>
					<PictureWrapper>{imageElement}</PictureWrapper>
					<ColorWrapper>
						{item.exteriorColor === null ? (
							<Skeleton.ColorTile />
						) : (
							item.exteriorColor.hexCode &&
							(isHovered ? (
								<Tooltip
									appendToBody
									content={`${formatMessage(listMessages.headerExteriorColor)}: ${item.exteriorColor.title}`}
								>
									<OnTopExteriorColorTile color={item.exteriorColor.hexCode} />
								</Tooltip>
							) : (
								<ExteriorColorTile color={item.exteriorColor.hexCode} />
							))
						)}
						{item.interiorColor === null ? (
							<Skeleton.ColorTile />
						) : (
							item.interiorColor.seatColor &&
							(isHovered ? (
								<Tooltip
									appendToBody
									content={`${formatMessage(listMessages.headerInteriorColor)}: ${item.interiorColor.title}`}
								>
									<OnTopInteriorColorTile
										seatColor={item.interiorColor.seatColor}
										cockpitColor={item.interiorColor.cockpitColor}
										seatStitchingColor={item.interiorColor.seatStitchingColor}
										cockpitStitchingColor={item.interiorColor.cockpitStitchingColor}
									/>
								</Tooltip>
							) : (
								<InteriorColorTile
									seatColor={item.interiorColor.seatColor}
									cockpitColor={item.interiorColor.cockpitColor}
									seatStitchingColor={item.interiorColor.seatStitchingColor}
									cockpitStitchingColor={item.interiorColor.cockpitStitchingColor}
								/>
							))
						)}
					</ColorWrapper>
					<ModelNameVinWrapper $alignItems="flex-start" $direction="column">
						{routes?.inventoryList?.vehicleDetails ? (
							<StyledTableRowLink
								to={routes.inventoryList.vehicleDetails.url(item.id)}
								$hasModelName={item.modelName !== null}
							>
								{modelNameElement}
							</StyledTableRowLink>
						) : (
							modelNameElement
						)}
						<OnTopCopyToClipboard value={item.vin ?? emptyPlaceholder} color="contrast-medium" compact />
					</ModelNameVinWrapper>
				</ImageColorModelWrapper>
			</TableCellFixed>
			<TableCellFixed $width={inventoryListColumns.inventoryStatus}>
				{isHovered ? (
					<Flex $gap="xs">
						<Tooltip appendToBody content={item.pvmsStatusHint}>
							<PText
								ellipsis
								color={mapInventoryStatusColor(item.stockStatus)}
								weight={item.stockStatus !== "INCOMING" ? "bold" : undefined}
							>
								<FormattedMessage {...stockStatusMessages[item.stockStatus]} /> {item.pvmsOrderStatus}
							</PText>
						</Tooltip>
						{item.vehicleOrder && (
							<>
								<StyledColorIndicator
									$color={item.vehicleOrder.errorCode ? colorError : colorSuccess}
									$size={8}
								/>
								<OnTopTooltip content={item.vehicleOrder?.statusMessage}>
									<OrderCodeTag>{item.vehicleOrder?.statusCode}</OrderCodeTag>
								</OnTopTooltip>
								{item.vehicleOrder.errorCode && (
									<OnTopTooltip
										content={
											item.vehicleOrder?.errorMessage
												? `${item.vehicleOrder.errorMessage} (${item.vehicleOrder?.errorCode})`
												: item.vehicleOrder?.errorCode
										}
									>
										<TagIcon compact color="error" iconName="error" />
									</OnTopTooltip>
								)}
							</>
						)}
					</Flex>
				) : (
					<Flex $gap="xs">
						<PText
							ellipsis
							color={mapInventoryStatusColor(item.stockStatus)}
							weight={item.stockStatus !== "INCOMING" ? "bold" : undefined}
						>
							<FormattedMessage {...stockStatusMessages[item.stockStatus]} /> {item.pvmsOrderStatus}
						</PText>
						{item.vehicleOrder && (
							<>
								<StyledColorIndicator
									$color={item.vehicleOrder.errorCode ? colorError : colorSuccess}
									$size={8}
								/>
								<OrderCodeTag>{item.vehicleOrder?.statusCode}</OrderCodeTag>
								{item.vehicleOrder.errorCode && <TagIcon compact color="error" iconName="error" />}
							</>
						)}
					</Flex>
				)}
				{item.stockStatusDate && (
					<Date size="xs" color="contrast-medium" view={item.stockStatusDate} ellipsis />
				)}
			</TableCellFixed>
			<TableCellFixed $width={inventoryListColumns.salesStatus}>
				<BadgeIconsWrapper>
					<BadgeWrapper>
						<StatusBadge status={item.status} />
					</BadgeWrapper>
					{item.violatedPublishingRules && <OnTopVisibilityIcon view={item.violatedPublishingRules} />}
					{item.onlineOrdering && <OnTopOnlineOrderingIcon view={item.onlineOrdering} hideLabel />}
					{item.listingScore &&
						(item.listingScore.completedCount ? (
							isHovered ? (
								<Tooltip
									appendToBody
									content={
										<>
											<PText weight="semibold">
												<FormattedMessage {...messages.listingQualityTooltipTitle} />
											</PText>
											<PText align="center">
												{item.listingScore.completedCount}&nbsp;/&nbsp;
												{item.listingScore.totalRequirementsCount}
											</PText>
										</>
									}
								>
									<OnTopListingScoreIcon color={item.listingScore.colorIndicator} />
								</Tooltip>
							) : (
								<ListingScoreIcon color={item.listingScore.colorIndicator} />
							)
						) : (
							<ListingScoreIcon color={item.listingScore.colorIndicator} />
						))}
				</BadgeIconsWrapper>
			</TableCellFixed>
			<TableCellFixed $width={inventoryListColumns.vehicleType}>
				{isHovered ? (
					<Tooltip appendToBody content={formatMessage(vehicleTypeMessages[item.vehicleType])}>
						<OnTopInlineBlock>
							<FormattedMessage {...vehicleTypeShortMessages[item.vehicleType]} />
						</OnTopInlineBlock>
					</Tooltip>
				) : (
					<FormattedMessage {...vehicleTypeShortMessages[item.vehicleType]} />
				)}
				{item.porscheApproved && (
					<PText
						ellipsis
						title={formatMessage(commonMessages.porscheApproved)}
						color="contrast-medium"
						size="xs"
					>
						<FormattedMessage {...commonMessages.porscheApprovedShort} />
					</PText>
				)}
			</TableCellFixed>
			{isFirstRegistrationDateAvailable ? (
				<TableCellFixed $width={inventoryListColumns.mileageFirstRegistrationDate}>
					<TextWrapBreak>{item.mileage}</TextWrapBreak>
					<Date view={item.firstRegistrationDate} />
				</TableCellFixed>
			) : (
				<TableCellFixed $width={inventoryListColumns.mileageModelYear}>
					<TextWrapBreak>{item.mileage}</TextWrapBreak>
					{item.modelYear ?? <Skeleton.Text $width="40px" />}
				</TableCellFixed>
			)}
			<TableCellFixed $width={inventoryListColumns.price}>
				{item.price ? (
					<>
						<PriceWrapper>
							{isHovered ? (
								<Tooltip
									appendToBody
									content={
										item.priceHistory.length > 0 ? (
											<PriceHistory view={item.priceHistory} />
										) : undefined
									}
								>
									<OnTopText weight="bold">{item.price.value}</OnTopText>
								</Tooltip>
							) : (
								<PText weight="bold">{item.price.value}</PText>
							)}
							{item.price.vatDeductible && (
								<PTag compact>
									<FormattedMessage {...commonMessages.vat} />
								</PTag>
							)}
						</PriceWrapper>
						<PText color="contrast-medium" size="xs">
							{item.price.lastPriceChange}
						</PText>
					</>
				) : routes?.inventoryList?.vehicleDetails ? (
					<OnTopInlineBlockVehicleDetailsStageLink
						to={routes.inventoryList.vehicleDetails.url(item.id)}
						state={{ editStage: "pricing" }}
						onClick={onAddPriceClick}
					>
						<PText ellipsis>
							<FormattedMessage {...messages.addPrice} />
						</PText>
					</OnTopInlineBlockVehicleDetailsStageLink>
				) : (
					emptyPlaceholder
				)}
			</TableCellFixed>
			<TableCellFixed $width={inventoryListColumns.location}>
				<Flex $direction="column" $alignItems="flex-start">
					{displayLocation &&
						(isHovered ? (
							<Tooltip appendToBody content={item.location.displayName}>
								<OnTopInlineBlockPText>{item.location.code}</OnTopInlineBlockPText>
							</Tooltip>
						) : (
							<PText>{item.location.code}</PText>
						))}
					{item.inventoryNumber ? (
						<OnTopCopyToClipboard
							maxWidth={inventoryListColumns.location - 20} // 20px for the icon
							value={item.inventoryNumber}
						/>
					) : routes?.inventoryList?.vehicleDetails ? (
						<OnTopInlineBlockVehicleDetailsStageLink
							to={routes.inventoryList.vehicleDetails.url(item.id)}
							state={{ editStage: "inventoryIntake" }}
							onClick={onAddStockNumberClick}
						>
							<PText ellipsis>
								<FormattedMessage {...messages.addInventoryNumber} />
							</PText>
						</OnTopInlineBlockVehicleDetailsStageLink>
					) : (
						emptyPlaceholder
					)}
				</Flex>
			</TableCellFixed>
		</TableRowWithLink>
	)
}

const mapInventoryStatusColor = (stockStatus: StockStatus): TextColor | undefined =>
	stockStatus === "INCOMING" ? undefined : "success"

const StyledTableRowLink = styled(TableRowLink, { shouldForwardProp })<{ $hasModelName: boolean }>`
	max-width: 100%;
	&::before {
		z-index: 1; /* needed to be above relative/absolute PTags */
	}
	/* PDS gives every anchor a frosted hover background, which reads as the skeleton itself changing */
	${({ $hasModelName }) => !$hasModelName && "&:hover { background-color: transparent; }"}
`

const PictureWrapper = styled.div`
	width: 75px;
	min-width: 75px;
`

const PriceWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${spacingStaticXs};
	white-space: nowrap;
`

const ImageColorModelWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: start;
	flex-direction: row;
	gap: ${spacingStaticSm};
`

const ColorWrapper = styled(ImageColorModelWrapper)`
	flex-direction: column;
`

const BadgeIconsWrapper = styled(ImageColorModelWrapper)`
	gap: ${spacingStaticXs};
`

const BadgeWrapper = styled.div`
	min-width: 80px;
`

const TextWrapBreak = styled(PText)`
	white-space: pre-wrap;
	word-break: break-all;
`

const OrderCodeTag = styled(({ children, ...props }: PropsWithChildren) => (
	<PTag {...props} compact>
		{children}
	</PTag>
))`
	margin-bottom: ${spacingStaticXs};
`

const OnTop = styled.div`
	position: relative;
	z-index: 2; /* to be on top of link with pseudo element */
`
const OnTopVisibilityIcon = OnTop.withComponent(VisibilityIcon)
const OnTopCopyToClipboard = OnTop.withComponent(CopyToClipboard)
const OnTopOnlineOrderingIcon = OnTop.withComponent(OnlineOrderingIcon)
const OnTopInteriorColorTile = OnTop.withComponent(InteriorColorTile)
const OnTopExteriorColorTile = OnTop.withComponent(ExteriorColorTile)
const OnTopListingScoreIcon = OnTop.withComponent(ListingScoreIcon)
const OnTopText = OnTop.withComponent(PText)
const OnTopTableCellFixed = OnTop.withComponent(TableCellFixed)
const OnTopTooltip = OnTop.withComponent(Tooltip)

const OnTopInlineBlock = styled(OnTop)`
	display: inline-block;
	max-width: 100%;
`

const StyledColorIndicator = styled(ColorIndicator)`
	margin-top: 8px;
`

const OnTopInlineBlockVehicleDetailsStageLink = OnTopInlineBlock.withComponent(VehicleDetailsStageLink)
const OnTopInlineBlockPText = OnTopInlineBlock.withComponent(PText)

const ModelNameVinWrapper = styled(Flex)`
	min-width: 0; /* stops overflow and makes truncation of modelName work */
`
